import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/SiteChrome";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

export default function Account() {
    const { user, logout } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);

    useEffect(() => {
        if (!user) return;
        api.get("/orders/mine")
            .then(({ data }) => setOrders(data.items))
            .catch(() => setOrders([]))
            .finally(() => setLoadingOrders(false));
    }, [user]);

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[hsl(var(--dust-50))]" data-testid="account-page">
            <Header />
            <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
                <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Account</div>
                <h1 className="mt-3 font-display text-5xl uppercase italic text-[hsl(var(--ink))]">Your basecamp.</h1>

                <div className="mt-10 grid gap-8 border-2 border-[hsl(var(--ink))] bg-card p-8 md:grid-cols-[auto_1fr]">
                    {user.picture ? (
                        <img src={user.picture} alt="" className="h-24 w-24 border-2 border-[hsl(var(--ink))] object-cover" />
                    ) : (
                        <div className="flex h-24 w-24 items-center justify-center border-2 border-[hsl(var(--ink))] bg-[hsl(var(--dust-300))] font-display text-4xl text-[hsl(var(--dust-50))]">
                            {user.name?.[0]?.toUpperCase() || "U"}
                        </div>
                    )}
                    <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">Signed in as</div>
                        <div className="mt-1 font-heading text-2xl uppercase text-[hsl(var(--ink))]" data-testid="account-name">{user.name}</div>
                        <div className="font-mono-brand text-sm text-[hsl(var(--ink-soft))]" data-testid="account-email">{user.email}</div>
                        <div className="mt-2 inline-block border border-[hsl(var(--dust-300))] px-2 py-1 font-mono-brand text-[10px] uppercase tracking-wider">
                            role: {user.role} · via {user.provider}
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid gap-4 md:grid-cols-3">
                    <Link to="/wishlist" className="border-2 border-[hsl(var(--ink))] bg-card p-6 hover:-translate-y-0.5 transition-transform">
                        <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))]">Wishlist</div>
                        <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">Parts you want on the truck.</p>
                    </Link>
                    <Link to="/bookings" className="border-2 border-[hsl(var(--ink))] bg-card p-6 hover:-translate-y-0.5 transition-transform">
                        <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))]">Bookings</div>
                        <p className="mt-2 text-sm text-[hsl(var(--ink-soft))]">Consults, install slots, trail days.</p>
                    </Link>
                    {user.role === "admin" && (
                        <Link to="/admin" className="border-2 border-[hsl(var(--primary))] bg-[hsl(var(--primary))] p-6 hover:-translate-y-0.5 transition-transform">
                            <div className="font-heading text-sm uppercase tracking-wider text-[hsl(var(--ink))]">Admin</div>
                            <p className="mt-2 text-sm text-[hsl(var(--ink))]">Dashboard, catalog, orders.</p>
                        </Link>
                    )}
                </div>

                <section className="mt-12" data-testid="account-orders">
                    <div className="flex items-baseline justify-between">
                        <h2 className="font-display text-3xl uppercase italic text-[hsl(var(--ink))]">Order history</h2>
                        <span className="font-mono-brand text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                            paid orders only
                        </span>
                    </div>

                    {loadingOrders ? (
                        <div className="mt-6 border border-[hsl(var(--dust-300))] bg-card p-8 font-mono-brand text-sm text-[hsl(var(--ink-soft))]">
                            Loading…
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="mt-6 border-2 border-dashed border-[hsl(var(--dust-300))] p-10 text-center" data-testid="account-orders-empty">
                            <div className="font-display text-3xl uppercase italic text-[hsl(var(--ink))]">No completed orders yet.</div>
                            <p className="mt-2 text-[hsl(var(--ink-soft))]">
                                Once you check out, your paid orders will show up here.
                            </p>
                            <Link to="/shop" className="mt-6 inline-block btn-primary">Find some gear →</Link>
                        </div>
                    ) : (
                        <div className="mt-6 space-y-4">
                            {orders.map((o) => (
                                <div key={o.order_id} className="border-2 border-[hsl(var(--ink))] bg-card p-5" data-testid={`account-order-${o.order_id}`}>
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                                                {new Date(o.created_at).toLocaleString()} · order #{o.order_id.slice(0, 8)}
                                            </div>
                                            <div className="mt-1 font-heading text-base uppercase text-[hsl(var(--ink))]">
                                                {o.items?.length || 0} item{(o.items?.length || 0) === 1 ? "" : "s"}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display text-2xl text-[hsl(var(--ink))]">${Number(o.amount || 0).toFixed(2)}</div>
                                            <div className="font-mono-brand text-[10px] uppercase tracking-wider text-[hsl(var(--success))]">
                                                ● paid
                                            </div>
                                        </div>
                                    </div>
                                    <ul className="mt-4 divide-y divide-[hsl(var(--dust-300))] border-t border-[hsl(var(--dust-300))]">
                                        {(o.items || []).map((it) => (
                                            <li key={it.slug} className="flex items-center justify-between py-2 font-mono-brand text-sm">
                                                <span className="text-[hsl(var(--ink))]">
                                                    {it.qty} × <Link to={`/shop/${it.slug}`} className="hover:underline">{it.name}</Link>
                                                </span>
                                                <span className="text-[hsl(var(--ink-soft))]">${(it.qty * it.price).toFixed(2)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <div className="mt-12">
                    <button onClick={logout} className="btn-ghost" data-testid="account-signout">Sign out</button>
                </div>
            </div>
            <Footer />
        </div>
    );
}
